<!-- Создает новый элемент справочника из селекта -->
<template>
  <BaseModal
    :visible="true"
    title="Создание элемента справочника"
    size="md"
    footer-align="start"
    elevated-footer
    @close="$emit('close')"
  >
    <form
      id="reference-item-create-form"
      class="modal-form"
      @submit.prevent="handleSubmit"
    >
      <div ref="formBodyRef">
        <!-- Название используется для label и генерации code элемента -->
        <BaseFormField
          label="Название"
          required
          :filled="!!formData.name"
          :error="errors.name"
          error-message="Обязательное поле"
        >
          <input
            v-model="formData.name"
            class="form-select"
            :class="{ invalid: errors.name }"
            placeholder="Например: Собственный"
            @input="errors.name = false"
          />
        </BaseFormField>

        <!-- Цвет помогает визуально отличать элементы справочника -->
        <BaseFormField label="Цвет">
          <div class="color-picker-wrapper">
            <input
              type="color"
              v-model="formData.color"
              class="color-picker"
            />
            <input
              v-model="formData.color"
              class="form-select color-input"
              placeholder="#22c55e"
            />
          </div>
        </BaseFormField>
      </div>
    </form>

    <template #footer>
      <base-primary-button
        type="submit"
        form="reference-item-create-form"
        :disabled="isSubmitting"
      >
        <span v-if="isSubmitting">Сохранение...</span>
        <span v-else>Сохранить</span>
      </base-primary-button>
      <base-button
        type="button"
        :disabled="isSubmitting"
        @click="$emit('close')"
      >
        Отмена
      </base-button>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { useReferenceItemsStore } from "@/stores/referenceItemsStore";
import { useNotificationStore } from "@/stores/notificationStore";
import { generateUniqueCode } from "@/utils/codeGenerator";

export default {
  name: "AddReferenceItemModal",
  props: {
    targetModel: {
      type: String,
      required: true,
    },
    targetField: {
      type: String,
      required: true,
    },
  },
  emits: ["close", "item-created"],
  data() {
    return {
      isSubmitting: false,
      formData: {
        name: "",
        color: "#22c55e",
      },
      errors: {
        name: false,
      },
    };
  },
  computed: {
    ...mapState(useReferenceItemsStore, ["items"]),

    notificationStore() {
      return useNotificationStore();
    },
  },
  watch: {
    "formData.name"(newName) {
      this.formData.code = generateUniqueCode(newName, this.items);
    },
  },
  methods: {
    ...mapActions(useReferenceItemsStore, [
      "createItem",
      "setTarget",
    ]),

    validate() {
      let isValid = true;
      if (!String(this.formData.name || "").trim()) {
        this.errors.name = true;
        isValid = false;
      }
      return isValid;
    },

    async handleSubmit() {
      if (!this.validate()) {
        this.notificationStore.showError(
          "Пожалуйста, заполните все обязательные поля"
        );

        this.$nextTick(() => {
          const firstError = this.$refs.formBodyRef?.querySelector(
            ".invalid, .error-message"
          );
          if (firstError) {
            const group = firstError.closest(".form-group");
            (group || firstError).scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        });

        return;
      }

      try {
        this.isSubmitting = true;

        this.setTarget(this.targetModel, this.targetField);

        const newItem = await this.createItem({
          name: this.formData.name.trim(),
          code:
            this.formData.code ||
            generateUniqueCode(this.formData.name, this.items),
          color: this.formData.color || null,
        });

        this.notificationStore.showSuccess("Элемент справочника создан");
        this.$emit("item-created", newItem);
        this.$emit("close");
      } catch {
        // ошибка обрабатывается в сторе
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-form {
  display: flex;
  flex-direction: column;
}

.color-picker-wrapper {
  display: flex;
  gap: 8px;
  align-items: center;
}

.color-picker {
  width: 40px;
  height: 38px;
  border: 1px solid #999;
  border-radius: 6px;
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
}

.color-input {
  flex: 1;
  font-family: "Menlo", "Monaco", "Courier New", monospace;
  font-size: 13px;
}

</style>
